import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { Box, Card, Grid, Typography } from '@mui/material';
import {
    AchatsStateType,
    CustomizerReducerType,
    DOSSIERS_SLICE_NAME,
    Dossier,
    DossierFormalite,
    DossiersStateType,
    FACTURES_ACHATS_SLICE_NAME,
    FactureAchat,
    Fournisseur,
    MediaObject,
    TiersStateType,
    setXIdSociete,
    useAchatsService,
    useDossierService,
    useFormaliteService,
    useTiersService,
} from '@europrocurement/l2d-domain';
import {
    PdfViewer,
    footerHeight,
    TopbarHeight,
    FlexyTabs,
    EmptyBox,
    FlexyHeaderForm,
    SkeletonLoader,
    SplitViewSkeleton,
    DataCardSkeleton,
    FormSkeleton,
} from '@europrocurement/flexy-components';
import type { FactureAchatLigneJsonldFactureAchatRead } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import {
    FactureVente,
    FACTURE_VENTE_SLICE_NAME,
} from '@europrocurement/l2d-domain/reducers/dossiers/slices/factureVenteSlice';
import { DataSource, EuroprocApiResponseStatus } from '@europrocurement/l2d-redux-utils';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import { jsonLdIdScraper } from '@europrocurement/l2d-utils';
import { useSnackbar } from 'notistack';
import { contactSupport, refreshPage } from '@europrocurement/l2d-utils/function-utils';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { useLoadingStatus } from '@europrocurement/l2d-hooks';
import { FormalityStateType } from '@europrocurement/l2d-domain/reducers/formalites/types';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { faClone, faRotate } from '@fortawesome/pro-duotone-svg-icons';
import useAddCustomContextActions from '../../../hooks/contextActions/useAddCustomContextActions';
import useDeleteInvoice from '../../../hooks/contextActions/useDeleteInvoice';
import useSyncG3ContextAction from '../../../hooks/contextActions/useSyncG3ContextAction';
import { AppDispatch } from '../../../../../redux/types';

import { CodeRejetsChipType } from '../../../components/widgets/CodeRejetsChipList';
import { useGetLignes, useGetRejets } from '../../../components/forms/functions/dataHooks';
import { AttachmentsWidget, AttachmentType } from '../../../components/widgets/AttachmentsWidget';
import { NotesFacture } from '../../../components/widgets/NotesFacture';
import NoteObject from '../../../components/notes/NoteObject';
import InvoiceDataCard from '../../../components/fragments/InvoiceDataCard/InvoiceDataCard';
import useHandleCleanIOInvoiceForm from '../../../../../hooks/useHandleCleanIOInvoiceForm';
import UpdateSimpleInvoiceForm from '../../../components/forms/simple/UpdateSimpleInvoiceForm';
import { useReindexOpenSearchFacture } from '../../../hooks/useReindexOpenSearchFacture';

const UpdateSimpleInvoiceView: React.FunctionComponent = function () {
    const { selectDossierFormalite } = useFormaliteService();
    const { selectFournisseur } = useTiersService();

    const { selectMediaobject, selectFactureAchat } = useAchatsService();
    const { selectDossier, selectFactureVente } = useDossierService();

    const { mediaobjectid, factureachatid } = useParams();
    useHandleCleanIOInvoiceForm({});

    const { setSidebarOpen } = useFullLayout();

    React.useEffect(() => {
        setSidebarOpen(false);
        return () => {
            setSidebarOpen(true);
        };
    }, [setSidebarOpen]);

    /** ********** */
    /* DataSources */
    /** ********** */
    const foliosDatasource: DataSource<Dossier> = useSelector(
        (s: DossiersStateType) => s.dossiers.dos.main,
        _.isEqual,
    );

    const foliosFormalityDatasource: DataSource<DossierFormalite> = useSelector(
        (s: FormalityStateType) => s.formalites.dosform.main,
        _.isEqual,
    );

    const publishersDatasource: DataSource<Fournisseur> = useSelector(
        (s: TiersStateType) => s.tiers.fournisseur.main,
        _.isEqual,
    );

    const sellsDatasource: DataSource<FactureVente> = useSelector(
        (s: DossiersStateType) => s.dossiers.facturevente.main,
        _.isEqual,
    );

    /** ********** */
    /* DataSources */
    /** ********** */

    /** ************* */
    /* Selected items */
    /** ************* */
    const mediaObject: MediaObject = useSelector(
        (s: AchatsStateType) => s.achats.mediaobject.main.selected,
        _.isEqual,
    );

    const factureAchatSelected: FactureAchat = useSelector(
        (s: AchatsStateType) => s.achats.factureachat.main.selected,
        _.isEqual,
    );

    const factureAchatSelectedStatus: EuroprocApiResponseStatus = useSelector(
        (s: AchatsStateType) => s.achats.factureachat.main.selectedStatus,
        _.isEqual,
    );

    const fournisseurSelected: Fournisseur | undefined = useSelector(
        (s: TiersStateType) => s.tiers.fournisseur.main.selected,
        _.isEqual,
    );

    const dossierSelected: Dossier | undefined = useSelector(
        (s: DossiersStateType) => s.dossiers.dos.main.selected,
        _.isEqual,
    );

    const dossierFormaliteSelected: DossierFormalite | undefined = useSelector(
        (s: FormalityStateType) => s.formalites.dosform.main.selected,
        _.isEqual,
    );
    /** ************* */
    /* Selected items */
    /** ************* */

    const [lignes, setLignes] = useState<FactureAchatLigneJsonldFactureAchatRead[]>([]);
    const [rejets, setRejets] = useState<CodeRejetsChipType[]>([]);

    const [lignesStatus, setLignesStatus] = useState<EuroprocApiResponseStatus>('idle');
    const [rejetsStatus, setRejetsStatus] = useState<EuroprocApiResponseStatus>('idle');

    const [isFormalite, setIsFormalite] = useState<boolean>();

    const dispatch = useDispatch<AppDispatch>();

    const { enqueueSnackbar } = useSnackbar();

    const roleChecker = UseKeycloakCheckRole();
    const isInterne = roleChecker('realm:interne');
    const isAdmin = roleChecker('api-achats:administrator');

    useEffect(() => {
        dispatch({
            type: `${DOSSIERS_SLICE_NAME}/deletemainSelected`,
        });
    }, [dispatch]);

    if (!factureachatid) {
        throw new Error('FactureAchatId is undefined !');
    }

    /** Si c'est la mauvaise facture, on la delete */
    useEffect(() => {
        if (factureAchatSelected && factureachatid !== factureAchatSelected?.id?.toString()) {
            dispatch({
                type: `${FACTURES_ACHATS_SLICE_NAME}/deletemainSelected`,
            });
        }
    }, [dispatch, factureAchatSelected, factureachatid]);

    /** va chercher la facture */
    useEffect(() => {
        if (!factureAchatSelected && factureAchatSelectedStatus !== 'loading') {
            dispatch(selectFactureAchat({ id: +factureachatid }));
        }
    }, [
        dispatch,
        factureAchatSelected,
        factureAchatSelectedStatus,
        factureachatid,
        selectFactureAchat,
    ]);

    /**
     * Select MediaObject corresponding url param mediaobjectid.
     *
     * Should select MediaObject on mount.
     */
    useEffect(() => {
        if (mediaobjectid) {
            dispatch(selectMediaobject({ id: +mediaobjectid }));
        } else {
            enqueueSnackbar(
                `Le PDF n'est peut-être pas le bon, car son identifiant n'a pas été trouvé dans l'url. ${refreshPage} ${contactSupport}`,
                {
                    variant: 'warning',
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Si le PDF a un idSociete,
     * alors le définir comme xIdSociete de l'utilisateur
     */
    useEffect(() => {
        if (factureAchatSelected?.idSociete) {
            dispatch(
                setXIdSociete(
                    factureAchatSelected?.idSociete as CustomizerReducerType['xIdSociete'],
                ),
            );
        }
    }, [dispatch, factureAchatSelected?.idSociete]);

    /**
     * Définir le fournisseur sélectionné
     */
    useEffect(() => {
        if (!factureAchatSelected || !factureAchatSelected.idEntiteFacturante) return;

        if (
            !fournisseurSelected ||
            fournisseurSelected.id !== factureAchatSelected.idEntiteFacturante
        ) {
            if (factureAchatSelected.idEntiteFacturante) {
                if (publishersDatasource.selectedStatus !== 'loading') {
                    dispatch(selectFournisseur({ id: factureAchatSelected.idEntiteFacturante }));
                }
            }
        }
    }, [
        dispatch,
        factureAchatSelected,
        publishersDatasource.selectedStatus,
        fournisseurSelected,
        selectFournisseur,
    ]);

    /**
     * A partir du fournisseur sélectionné,
     * déterminer le domaine.
     */
    useEffect(() => {
        if (fournisseurSelected?.ddmId !== undefined && fournisseurSelected?.ddmId >= 0) {
            setIsFormalite(fournisseurSelected?.ddmId === 3);
        }
    }, [fournisseurSelected]);

    /**
     * Définir le dossier.
     * Dépends du domaine définit à la sélection du fournisseur.
     */
    useEffect(() => {
        if (isFormalite === undefined || isFormalite) return;

        if (foliosDatasource.selectedStatus !== 'loading' && lignes && lignes[0]) {
            if (!dossierSelected) {
                if (lignes[0].idDossier === null) {
                    setIsFormalite(true);
                    return;
                }
                dispatch(
                    selectDossier({
                        id: `${lignes[0].idDossier}`,
                    }),
                );
            }
        }
    }, [
        dispatch,
        dossierSelected,
        foliosDatasource.selectedStatus,
        isFormalite,
        lignes,
        selectDossier,
    ]);

    /**
     * Définir la facture de vente.
     * Dépends du domaine définit à la sélection du fournisseur.
     */
    useEffect(() => {
        if (isFormalite === undefined || isFormalite) return;

        let idFactureVente: number;

        if (dossierSelected && dossierSelected.facture) {
            idFactureVente = jsonLdIdScraper(dossierSelected.facture);
            if (sellsDatasource.selectedStatus !== 'loading') {
                if (!sellsDatasource.selected || sellsDatasource.selected.id !== idFactureVente) {
                    dispatch(selectFactureVente({ id: idFactureVente }));
                }
            }
        } else {
            dispatch({
                type: `${FACTURE_VENTE_SLICE_NAME}/deletemainSelected`,
            });
        }
    }, [
        dispatch,
        dossierSelected,
        isFormalite,
        selectFactureVente,
        sellsDatasource.selected,
        sellsDatasource.selectedStatus,
    ]);

    /**
     * Définir le dossier de formalité.
     * Dépends du domaine définit à la sélection du fournisseur.
     */
    useEffect(() => {
        if (isFormalite === undefined || !isFormalite) return;

        if (foliosFormalityDatasource.selectedStatus !== 'loading' && lignes && lignes[0]) {
            if (
                !dossierFormaliteSelected ||
                dossierFormaliteSelected.id !== lignes[0].idDossierFpro
            ) {
                if (lignes[0].idDossierFpro === null) {
                    setIsFormalite(false);
                }

                dispatch(
                    selectDossierFormalite({
                        id: `${lignes[0].idDossierFpro}`,
                    }),
                );
            }
        }
    }, [
        dispatch,
        dossierFormaliteSelected,
        foliosFormalityDatasource.selectedStatus,
        isFormalite,
        lignes,
        selectDossierFormalite,
    ]);

    const getRejets = useGetRejets();
    const getLignes = useGetLignes();

    const fetchLines = useCallback(() => {
        if (factureAchatSelected?.id && lignesStatus !== 'loading') {
            setLignesStatus('loading');
            getLignes(factureAchatSelected.id, [])
                .then((data) => {
                    setLignesStatus('succeeded');
                    setLignes(data);
                })
                .catch((error) => {
                    setLignesStatus('failed');
                    console.error(error);
                });
        }
    }, [factureAchatSelected?.id, getLignes, lignesStatus]);

    useEffect(() => {
        if (factureAchatSelected && lignesStatus !== 'succeeded') {
            fetchLines();
        }
    }, [factureAchatSelected, fetchLines, lignesStatus]);

    useEffect(() => {
        if (
            factureAchatSelected &&
            factureAchatSelected.id &&
            rejetsStatus !== 'loading' &&
            rejetsStatus !== 'succeeded'
        ) {
            setRejetsStatus('loading');
            getRejets(factureAchatSelected.id, []).then((data) => {
                setRejetsStatus('succeeded');
                setRejets(data);
            });
        }
    }, [factureAchatSelected, getRejets, rejetsStatus]);

    const syncG3 = useSyncG3ContextAction({
        invoice: factureAchatSelected,
    });

    const deleteInvoice = useDeleteInvoice({
        invoice: factureAchatSelected,
    });

    const { reindexOpenSearchFacture } = useReindexOpenSearchFacture();

    const customContextActions = [];
    if (isAdmin) {
        customContextActions.push({
            name: 'Réindexation OpenSearch',
            icon: (
                <FaOptionIcon
                    icon={faClone}
                    option={faRotate}
                    size="1x"
                    color="blueGrey.main"
                    optionColor="blueGrey.dark"
                    iconProps={{
                        radius: 100,
                    }}
                />
            ),
            action: () => {
                if (factureAchatSelected) {
                    reindexOpenSearchFacture(factureAchatSelected);
                }
            },
        });
    }
    if (isInterne) {
        customContextActions.push(...[syncG3, deleteInvoice]);
    }

    useAddCustomContextActions(customContextActions);

    const readyToDisplayForm = useMemo<boolean>(() => {
        if (
            lignesStatus !== 'succeeded' ||
            rejetsStatus !== 'succeeded' ||
            factureAchatSelectedStatus !== 'succeeded'
        ) {
            // console.log('not ready loading status');
            return false;
        }

        if (!factureAchatSelected) {
            // console.log('not ready no facture');
            return false;
        }

        if (!fournisseurSelected) {
            // console.log('not ready no fournisseur');
            return false;
        }

        if (!dossierSelected && !dossierFormaliteSelected) {
            // console.log('not ready no dossier');
            return false;
        }

        if (
            factureAchatSelected.idEntiteFacturante &&
            factureAchatSelected.idEntiteFacturante !== 0
        ) {
            if (fournisseurSelected.id !== factureAchatSelected.idEntiteFacturante) {
                // console.log('not ready no idEntiteFacturante');
                return false;
            }
        }

        if (dossierSelected && lignes && lignes.length > 0 && lignes[0].idDossierFpro === null) {
            let idFactureVente: number;
            if (dossierSelected?.facture && dossierSelected.facture) {
                idFactureVente = jsonLdIdScraper(dossierSelected.facture);
                if (!sellsDatasource.selected) {
                    // console.log('not ready dossier has not facture');
                    return false;
                }
                if (sellsDatasource.selected.id !== idFactureVente) {
                    // console.log('not ready no vente');
                    return false;
                }
            }
        } else if (
            dossierFormaliteSelected &&
            lignes &&
            lignes.length > 0 &&
            lignes[0].idDossier === null
        ) {
            const dossiersFProToLoad = _.uniq(lignes.map((ligne) => ligne.idDossierFpro));

            if (dossiersFProToLoad.length > 1) {
                console.error('Multi dossier à implémenter');
            } else if (dossiersFProToLoad.length === 1) {
                if (dossierFormaliteSelected.id !== dossiersFProToLoad[0]) {
                    // console.log('not ready no dossier fpro');
                    return false;
                }
            }
        } else {
            // console.log('no lines');
            return false;
        }

        return true;
    }, [
        lignesStatus,
        rejetsStatus,
        factureAchatSelectedStatus,
        factureAchatSelected,
        fournisseurSelected,
        dossierSelected,
        dossierFormaliteSelected,
        lignes,
        sellsDatasource.selected,
    ]);

    const readyToDisplayDataCard = useMemo<boolean>(
        () => !!(factureAchatSelected && rejets && rejetsStatus === 'succeeded'),
        [factureAchatSelected, rejets, rejetsStatus],
    );

    const waitForValues = useMemo<boolean>(
        () =>
            !!(
                mediaObject &&
                factureAchatSelected &&
                fournisseurSelected &&
                (dossierSelected || dossierFormaliteSelected)
            ),
        [
            mediaObject,
            factureAchatSelected,
            dossierSelected,
            dossierFormaliteSelected,
            fournisseurSelected,
        ],
    );

    const { loading: globalLoading } = useLoadingStatus({ checkReady: () => waitForValues });
    const { loading: dataCardLoading } = useLoadingStatus({
        checkReady: () => readyToDisplayDataCard,
    });
    const { loading: formLoading } = useLoadingStatus({ checkReady: () => readyToDisplayForm });

    return (
        <SkeletonLoader
            isLoading={globalLoading}
            skeleton={<SplitViewSkeleton />}
        >
            <Grid container>
                <Grid
                    item
                    lg={6}
                    sm={12}
                >
                    <Card
                        id="cardPdfReader"
                        sx={{
                            height: `calc(100vh -${footerHeight} - ${TopbarHeight}  )`,
                        }}
                    >
                        <PdfViewer
                            title={mediaObject?.originalName}
                            pdfUrl={mediaObject?.contentUrl ?? undefined}
                            verticalScroll={false}
                        />
                    </Card>
                </Grid>
                <Grid
                    item
                    lg={6}
                    sm={12}
                >
                    <Card
                        sx={{
                            gap: '15px',
                            padding: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <SkeletonLoader
                            isLoading={dataCardLoading}
                            skeleton={<DataCardSkeleton />}
                        >
                            <InvoiceDataCard
                                invoice={factureAchatSelected}
                                rejectCodes={rejets}
                            />
                        </SkeletonLoader>
                    </Card>
                    <Card>
                        <SkeletonLoader
                            isLoading={formLoading}
                            skeleton={<FormSkeleton />}
                        >
                            <FlexyTabs
                                scrollbar={false}
                                tabs={[
                                    {
                                        tabName: 'facture',
                                        tabTitle: 'Facture',
                                        tabPanel: (
                                            <UpdateSimpleInvoiceForm
                                                mediaObject={mediaObject}
                                                invoice={factureAchatSelected}
                                                lines={lignes}
                                                fetchLines={fetchLines}
                                                publisher={fournisseurSelected}
                                                folioPublication={dossierSelected}
                                                folioFormality={dossierFormaliteSelected}
                                            />
                                        ),
                                    },
                                    {
                                        tabName: 'notes',
                                        tabTitle: 'Notes',
                                        tabPanel: (
                                            <>
                                                <FlexyHeaderForm
                                                    label={
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h2"
                                                                color="secondary"
                                                            >
                                                                Note du dossier
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                {dossierSelected && dossierSelected.notes ? (
                                                    <NoteObject
                                                        note={{
                                                            '@id': _.uniqueId(),
                                                            note: dossierSelected.notes,
                                                        }}
                                                        isEditable={false}
                                                    />
                                                ) : (
                                                    <EmptyBox
                                                        text="Pas de notes"
                                                        sx={{
                                                            marginTop: '10px',
                                                        }}
                                                    />
                                                )}
                                                <NotesFacture
                                                    title="Notes libres"
                                                    facture={factureAchatSelected}
                                                    idDossier={
                                                        dossierSelected?.id ||
                                                        dossierFormaliteSelected?.id ||
                                                        undefined
                                                    }
                                                />
                                            </>
                                        ),
                                    },
                                    {
                                        tabName: 'attachments',
                                        tabTitle: 'Fichiers joints',
                                        tabPanel: (
                                            <>
                                                <FlexyHeaderForm
                                                    label={
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h2"
                                                                color="secondary"
                                                            >
                                                                Fichiers joints
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                <AttachmentsWidget
                                                    attachments={
                                                        factureAchatSelected?.attachments as AttachmentType[]
                                                    }
                                                />
                                            </>
                                        ),
                                    },
                                ]}
                                routerParameterName="factureTab"
                            />
                        </SkeletonLoader>
                    </Card>
                </Grid>
            </Grid>
        </SkeletonLoader>
    );
};

export default UpdateSimpleInvoiceView;
